// Thin fetch wrapper — adds the bearer token, parses JSON, raises on !ok.
//
// Same-origin in prod (`dreambook.studio/api/*`); dev uses Vite's /api proxy
// to the local FastAPI on :8402. No base URL needed. On 401 the wrapper
// silently rotates the access token via the httpOnly refresh cookie
// (PRD-ACC-001.3) and retries once before surfacing the error.

import {
  applyTokenResponse,
  clearToken,
  getToken,
  type TokenResponse,
} from "./auth";

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public body?: unknown,
  ) {
    super(`${status} ${detail}`);
  }
}

type Json = Record<string, unknown> | Array<unknown> | null;

// v2.29 — only `/api/auth/me` clears local auth when refresh fails. Other
// endpoints may 401 for reasons unrelated to session liveness; we still try
// refresh+retry on any 401 before surfacing the error.
function _isAuthCheckPath(path: string): boolean {
  return path === "/api/auth/me" || path.startsWith("/api/auth/me?");
}

let refreshInflight: Promise<boolean> | null = null;

async function fetchRefreshedToken(): Promise<boolean> {
  try {
    const res = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) return false;
    const body = (await res.json()) as TokenResponse;
    applyTokenResponse(body);
    return true;
  } catch {
    return false;
  }
}

/** Rotate the access token using the httpOnly refresh cookie.
 *
 *  Cross-tab safety (2026-07 E2E fix): the refresh token is strictly one-time-use
 *  server-side — `auth_svc.refresh_session` revokes the presented token before
 *  minting a new pair, with NO reuse-tolerance window — so two tabs refreshing
 *  concurrently could burn the shared cookie twice and sign both out. The
 *  in-flight promise serializes within a tab; `navigator.locks` (a cross-tab
 *  mutex, supported everywhere the app runs, with a plain fallback elsewhere)
 *  serializes across tabs. Inside the lock we first check whether another tab
 *  already rotated the token while we waited, and adopt its result instead of
 *  spending the cookie again. */
export async function refreshSession(): Promise<boolean> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      const staleToken = getToken();
      const attempt = async (): Promise<boolean> => {
        const current = getToken();
        if (current && current !== staleToken) return true; // another tab refreshed first
        return fetchRefreshedToken();
      };
      if (typeof navigator !== "undefined" && navigator.locks) {
        return await navigator.locks.request("dreambook.auth.refresh", attempt);
      }
      return await attempt();
    } catch {
      return false;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

/** Map a gateway failure with a non-JSON body to a readable message. The CloudFront/ALB 5xx
 *  pages that fire on a gateway timeout are HTML, so they never parse as JSON — without this
 *  the raw JSON.parse threw a SyntaxError and the UI fell back to "Something went wrong." */
function gatewayMessage(status: number, statusText: string): string {
  if (status === 502) return "The server is temporarily unavailable (502). Please try again in a moment.";
  if (status === 503) return "The service is temporarily unavailable (503). Please try again in a moment.";
  if (status === 504)
    // Never tell an author to refresh here: the request that 504s is a long generation, and
    // everything they have typed and not yet saved lives in the page. A reload is the one
    // action that destroys it. The work usually is still running server-side.
    return "The gateway stopped waiting for the server (504). Everything on this page is still here — don't reload, or unsaved work is lost. The server may still be finishing; wait a moment and try again.";
  return statusText ? `Request failed (${status} ${statusText}).` : `Request failed (${status}).`;
}

/** Read + parse a response body without ever throwing a raw SyntaxError. A non-JSON body
 *  (gateway error page, truncated response) becomes a structured ApiError so callers that
 *  branch on `instanceof ApiError` get a real status + message instead of a generic error. */
async function parseResponse<T>(res: Response): Promise<T> {
  const text = await res.text();
  let parsed: unknown = null;
  let parseFailed = false;
  if (text) {
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      parseFailed = true;
    }
  }
  if (!res.ok) {
    const detail = parseFailed
      ? gatewayMessage(res.status, res.statusText)
      : extractDetail(parsed, res.statusText);
    throw new ApiError(res.status, detail, parseFailed ? text : parsed);
  }
  if (parseFailed) {
    throw new ApiError(res.status, "The server returned an unreadable response. Please try again.", text);
  }
  return parsed as T;
}

/** fetch() that converts a transport failure (DNS, connection refused, CORS, offline) into a
 *  structured ApiError(0) instead of a bare TypeError that surfaces as "Something went wrong." */
async function safeFetch(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(path, init);
  } catch (e) {
    throw new ApiError(0, "Can't reach the server. Check your connection and try again.", e);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: Json,
  opts: {
    auth?: boolean;
    _retried?: boolean;
    /** Extra request headers. Threaded through the retry path with the rest of
     *  `opts`, so a header-carried credential survives a token refresh. Used by
     *  the print-cover client to pass `X-Remote-Token` — a secret belongs in a
     *  header, never in the URL where it would reach logs and history. */
    headers?: Record<string, string>;
  } = { auth: true },
): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.auth !== false) {
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
  }

  const res = await safeFetch(path, {
    method,
    headers,
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // Expired access token — try the refresh cookie once, then retry. Only
  // clear local auth state when refresh itself fails (session truly dead).
  if (
    res.status === 401
    && opts.auth !== false
    && !opts._retried
    && path !== "/api/auth/refresh"
  ) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return request<T>(method, path, body, { ...opts, _retried: true });
    }
    if (_isAuthCheckPath(path)) {
      clearToken();
    }
  }

  return parseResponse<T>(res);
}

// Multipart form-data upload. Different from `post()` because we must NOT
// set Content-Type ourselves — the browser sets it with the correct
// multipart boundary. Used for audio-brief upload, image uploads, etc.
async function requestForm<T>(
  path: string,
  form: FormData,
  opts: { auth?: boolean; _retried?: boolean } = { auth: true },
): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.auth !== false) {
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
  }
  const res = await safeFetch(path, { method: "POST", headers, credentials: "include", body: form });
  if (
    res.status === 401
    && opts.auth !== false
    && !opts._retried
    && path !== "/api/auth/refresh"
  ) {
    const refreshed = await refreshSession();
    if (refreshed) return requestForm<T>(path, form, { ...opts, _retried: true });
    if (_isAuthCheckPath(path)) clearToken();
  }
  return parseResponse<T>(res);
}

/** Pull a human-readable message out of a FastAPI error body.
 *  FastAPI may return:
 *    {"detail": "plain string"}        ← 400, 402, custom HTTPExceptions
 *    {"detail": [{"loc": [...], "msg": "..."}, …]}  ← 422 validation
 *    {"detail": {"error": "...", ...}} ← custom structured detail
 *    {"error": {"code": "...", "message": "..."}} ← BR-004.2 envelope
 *  We flatten any of those to a readable sentence rather than letting
 *  JS call `String(obj)` and render "[object Object]" in the UI. */
function extractDetail(parsed: unknown, fallback: string): string {
  if (!parsed || typeof parsed !== "object") return fallback || "Request failed";
  const d = (parsed as { detail?: unknown }).detail;
  if (d == null) {
    // BR-004.2 error envelope: { error: { code, message } }.
    const err = (parsed as { error?: { message?: unknown; code?: unknown } }).error;
    if (err && typeof err === "object") {
      if (typeof err.message === "string") return err.message;
      if (typeof err.code === "string") return err.code;
    }
    return fallback || "Request failed";
  }
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    // FastAPI 422 shape — take the first validation error's msg.
    const first = d[0] as { msg?: string; loc?: unknown[] } | undefined;
    if (first?.msg) {
      const where = Array.isArray(first.loc) ? first.loc.filter((p) => p !== "body").join(".") : "";
      return where ? `${where}: ${first.msg}` : first.msg;
    }
    try { return JSON.stringify(d[0]); } catch { return fallback; }
  }
  if (typeof d === "object") {
    const obj = d as Record<string, unknown>;
    if (typeof obj.message === "string") return obj.message;
    if (typeof obj.error === "string")   return obj.error;
    try { return JSON.stringify(d); } catch { return fallback; }
  }
  return fallback || "Request failed";
}

/** Authed GET that returns the raw bytes (with the same 401→refresh→retry as `request`).
 *  Needed for downloads: a bare <a href>/window.open can't carry the bearer token, so a
 *  private book's file 404s ("Book not found") on direct navigation. Fetch it here instead,
 *  then hand the caller a Blob it can turn into an object URL. */
async function requestBlob(
  path: string,
  opts: { _retried?: boolean } = {},
): Promise<Blob> {
  const headers: Record<string, string> = {};
  const t = getToken();
  if (t) headers["Authorization"] = `Bearer ${t}`;
  const res = await safeFetch(path, { method: "GET", headers, credentials: "include" });
  if (res.status === 401 && !opts._retried && (await refreshSession())) {
    return requestBlob(path, { _retried: true });
  }
  if (!res.ok) {
    const detail = extractDetail(
      await res.json().catch(() => null),
      res.statusText,
    );
    throw new ApiError(res.status, detail);
  }
  return res.blob();
}

/** The download filename from a Content-Disposition header (RFC 5987 `filename*` first,
 *  then plain `filename=`), or null when the header carries neither. */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=utf-8''([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* malformed percent-encoding — fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

/** `requestBlob`, but also surfaces the server-chosen download filename so callers can
 *  name the saved file what the API named it (e.g. the book title, not "book.pdf"). */
async function requestBlobNamed(
  path: string,
  opts: { _retried?: boolean } = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const headers: Record<string, string> = {};
  const t = getToken();
  if (t) headers["Authorization"] = `Bearer ${t}`;
  const res = await safeFetch(path, { method: "GET", headers, credentials: "include" });
  if (res.status === 401 && !opts._retried && (await refreshSession())) {
    return requestBlobNamed(path, { _retried: true });
  }
  if (!res.ok) {
    const detail = extractDetail(
      await res.json().catch(() => null),
      res.statusText,
    );
    throw new ApiError(res.status, detail);
  }
  return {
    blob: await res.blob(),
    filename: filenameFromDisposition(res.headers.get("content-disposition")),
  };
}

/** Authed POST that returns the raw bytes (same 401→refresh→retry as `requestBlob`).
 *  Used for narration: the endpoint takes a JSON body and answers with audio/mpeg. */
async function requestBlobPost(
  path: string,
  body: Json,
  opts: { _retried?: boolean } = {},
): Promise<Blob> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const t = getToken();
  if (t) headers["Authorization"] = `Bearer ${t}`;
  const res = await safeFetch(path, {
    method: "POST", headers, credentials: "include", body: JSON.stringify(body),
  });
  if (res.status === 401 && !opts._retried && (await refreshSession())) {
    return requestBlobPost(path, body, { _retried: true });
  }
  if (!res.ok) {
    const detail = extractDetail(
      await res.json().catch(() => null),
      res.statusText,
    );
    throw new ApiError(res.status, detail);
  }
  return res.blob();
}

export interface ReqOpts {
  auth?: boolean;
  headers?: Record<string, string>;
}

export const api = {
  get:     <T>(p: string, o?: ReqOpts) => request<T>("GET", p, undefined, o),
  post:    <T>(p: string, b?: Json, o?: ReqOpts) => request<T>("POST", p, b, o),
  put:     <T>(p: string, b?: Json, o?: ReqOpts) => request<T>("PUT", p, b, o),
  patch:   <T>(p: string, b?: Json, o?: ReqOpts) => request<T>("PATCH", p, b, o),
  del:     <T>(p: string, o?: ReqOpts) => request<T>("DELETE", p, undefined, o),
  delete:  <T>(p: string, o?: ReqOpts) => request<T>("DELETE", p, undefined, o),
  postForm: requestForm,
  getBlob:  requestBlob,
  getBlobNamed: requestBlobNamed,
  postBlob: requestBlobPost,
};
